import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { PaylinkFormModal } from '../pages/paylinks/PaylinkFormModal';

export interface PaylinkFormPrefill {
  title?: string;
  amount?: number;
  currency?: string;
  description?: string;
}

export interface PaylinkFormHandle {
  setFields: (values: PaylinkFormPrefill) => void;
}

interface PaylinkFormContextValue {
  openCreate: () => void;
  close: () => void;
  isOpen: boolean;
  formRef: MutableRefObject<PaylinkFormHandle | null>;
}

const PaylinkFormContext = createContext<PaylinkFormContextValue | null>(null);

export function PaylinkFormProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const formRef = useRef<PaylinkFormHandle | null>(null);

  const openCreate = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo<PaylinkFormContextValue>(
    () => ({ openCreate, close, isOpen, formRef }),
    [openCreate, close, isOpen],
  );

  return (
    <PaylinkFormContext.Provider value={value}>
      {children}
      <PaylinkFormModal ref={formRef} isOpen={isOpen} onClose={close} />
    </PaylinkFormContext.Provider>
  );
}

export function usePaylinkForm() {
  const ctx = useContext(PaylinkFormContext);
  if (!ctx) throw new Error('usePaylinkForm must be used inside PaylinkFormProvider');
  return ctx;
}
